import React, { useState, useRef, useEffect } from 'react';
import { Editor, EditorState, CompositeDecorator, Modifier } from 'draft-js';
import Select from 'react-select';

import {
  editorStateFromJsonWithDecorator,
  getContentStateWithAppliedEntity,
  createEmptySelectionState,
  contentStateWithInsertedText,
  editorStateToJsonString,
} from 'Shared/utils/draftJsHelpers';

type TagComponentProps = {
  offsetkey;
  children;
};

const TagComponent = ({ offsetkey, children }: TagComponentProps) => {
  const style = {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    padding: '2px 0',
  };

  return (
    <span data-offset-key={offsetkey} style={style} contentEditable={false} className="unselectable">
      {children}
    </span>
  );
};

type MessageTemplateBuilderProps = {
  propertyOptions;
  messageTemplate;
  onMessageTemplateChange;
};

const MessageTemplateBuilder = ({
  propertyOptions,
  messageTemplate,
  onMessageTemplateChange,
}: MessageTemplateBuilderProps) => {
  const immutableEntityStrategy = (contentBlock, callback, contentState) => {
    contentBlock.findEntityRanges(character => {
      const entityKey = character.getEntity();
      if (entityKey === null) {
        return false;
      }
      return contentState.getEntity(entityKey).getMutability() === 'IMMUTABLE';
    }, callback);
  };

  const decorator = new CompositeDecorator([
    {
      strategy: immutableEntityStrategy,
      component: TagComponent,
    },
  ]);

  const [editorState, setEditorState] = useState(
    messageTemplate ? editorStateFromJsonWithDecorator(messageTemplate, decorator) : EditorState.createEmpty(decorator)
  );
  const [property] = useState(null);
  const [focusFlag, setFocusFlag] = useState(false);
  const editorRef = useRef<HTMLInputElement>(null);

  const setEditorStateAndUpdateMessageTemplate = newEditorState => {
    setEditorState(newEditorState);
    onMessageTemplateChange(editorStateToJsonString(newEditorState));
  };

  useEffect(() => {
    if (focusFlag && editorRef && editorRef.current) {
      editorRef.current.focus();
      setFocusFlag(false);
    }
  }, [focusFlag]);

  const addTag = propertyForTag => {
    const label = `[[${propertyForTag.value}]]`;
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const blockKey = selectionState.getFocusKey();
    const blockOffset = selectionState.getFocusOffset();

    const selectionForAnchor = createEmptySelectionState(blockKey, blockOffset, blockOffset);
    const contentStateWithAnchor = contentStateWithInsertedText(contentState, selectionForAnchor, label);

    const selectionStateForEntity = createEmptySelectionState(blockKey, blockOffset, blockOffset + label.length);

    const contentStateWithCreatedEntity = getContentStateWithAppliedEntity(
      contentStateWithAnchor,
      selectionStateForEntity,
      'RULE_BUILDER_MESSAGE_TAG',
      { label }
    );

    let newEditorState = EditorState.push(editorState, contentStateWithCreatedEntity);
    // Insert spaces to left and right of the tag
    for (let i = 0; i < 2; i += 1) {
      const selectionState2 = newEditorState.getSelection();
      const blockKey2 = selectionState2.getFocusKey();
      const blockOffset2 =
        i === 0 ? selectionState2.getFocusOffset() - label.length : selectionState2.getFocusOffset() + label.length;
      const selectionForAnchor2 = createEmptySelectionState(blockKey2, blockOffset2, blockOffset2);
      const cs = Modifier.insertText(newEditorState.getCurrentContent(), selectionForAnchor2, ' ');
      newEditorState = EditorState.push(newEditorState, cs, 'insert-text');
    }
    setEditorStateAndUpdateMessageTemplate(newEditorState);
    setFocusFlag(true);
  };

  return (
    <>
      <div className="card mb-4 p-2">
        <Editor
          spellCheck
          ref={editorRef}
          editorState={editorState}
          onChange={setEditorStateAndUpdateMessageTemplate}
        />
      </div>
      <div style={{ width: '340px' }}>
        <Select
          className="property mr-2"
          placeholder="Add Property to Display in Message"
          options={propertyOptions}
          value={property}
          onChange={p => addTag(p)}
        />
      </div>
    </>
  );
};

export default MessageTemplateBuilder;
